<template>
  <v-container fluid class="performance-estimate-wrapper mt-1 p-0">
    <asc-header
      class="performance-estimate-header"
      uppercase
      size="h3"
      value="Performance Estimate"
    >
    </asc-header>
    <v-row no-gutters>
      <v-col>
        <div v-if="!isReadOnly" class="estimate-message">
          <span>{{ getEstimateMessage }}</span>
          <span class="limitation-text-accent">
            {{ getLimitaionMessage }}
          </span>
          <v-tooltip top color="black">
            <template #activator="{ on, attrs }">
              <v-icon class="icon-info-limit" dark v-bind="attrs" v-on="on">
                mdi-information
              </v-icon>
            </template>
            <div style="font-size: 12px">
              <div>
                <v-icon class="tooltip-content-icon"> mdi-information </v-icon>
                <span>
                  The Performance estimate is restricted to logic with only 1
                  feature, e.g.
                </span>
                <div class="tooltip-content-icon-text">
                  "alert me if the mean oil temperature in cruise is above 600
                  degrees"<br />(AVERAGE([A048_CTLAXPW] WHERE (FLIGHTPHASE(7)))
                  > 600), so and/<br />or logic is not permitted.
                </div>
              </div>
              <div class="tooltip-content-text">
                <v-icon class="tooltip-content-icon">mdi-information</v-icon>
                <span>
                  The performance estimate dataset is limited to the first 50
                  effective
                </span>
                <div class="tooltip-content-icon-text">
                  aircraft selected in the configuration dropdown, above.
                </div>
              </div>
              <div class="tooltip-content-text">
                <v-icon class="tooltip-content-icon">mdi-information</v-icon>
                <span>
                  The performance estimate dataset is limited to the last 6
                  months from
                </span>
                <div class="tooltip-content-icon-text">
                  present day of the estimate generation.
                </div>
              </div>
            </div>
          </v-tooltip>
        </div>
        <div
          v-if="showLookbackWarning && !isReadOnly"
          class="lookback-validation"
        >
          <div class="icon">
            <v-icon size="16" color="#AB7303">mdi-alert</v-icon>
            <asc-label
              class="text"
              size="sm"
              :value="getLookbackValidationMessage"
            ></asc-label>
          </div>
        </div>
        <div v-if="isLookBackReadOnly" class="lookback-validation">
          <div class="icon">
            <v-icon size="16" color="#AB7303">mdi-alert</v-icon>
            <asc-label
              class="text"
              size="sm"
              :value="getLookbackReadOnlyMessage"
            ></asc-label>
          </div>
        </div>
        <div>
          <v-row no-gutters>
            <v-col
              v-if="lookBackResponse.displayErrorNotificationMessage"
              cols="12"
            >
              <asc-notification
                :definition="error"
                :on-close="databricksResponseErrorClose"
              />
            </v-col>
            <v-col cols="12">
              <div
                v-if="!isLookBackReadOnly"
                class="performance-estimate-style"
              >
                <v-row no-gutters>
                  <v-col cols="12" md="auto">
                    <asc-tooltip
                      top
                      :max-width="300"
                      :tooltip="
                        !enableRunPerformanceEstimate ||
                        (!displaySuccessMsg && getPerformanceLimitReached)
                          ? 'Enter Aircraft, Part Number(s), and a valid Frequency and Logic String to run a performance estimate.'
                          : getPerformanceLimitReached && !displaySuccessMsg
                          ? runLimitTooltipMsg
                          : ''
                      "
                    >
                      <asc-accent-button
                        v-if="displaySuccessMsg || !isReadOnly"
                        secondary
                        block
                        :button-text="estimateButtonName"
                        :on-click="handlePerformanceEstimateClick"
                        :loading="validateLoading"
                        :disabled="
                          !enableRunPerformanceEstimate ||
                          (!displaySuccessMsg && getPerformanceLimitReached)
                        "
                      />
                    </asc-tooltip>
                  </v-col>
                  <v-col v-if="displaySuccessMsg">
                    <div class="successTextMsg">
                      <v-icon class="checkIconColor">{{ 'mdi-check' }}</v-icon>
                      A performance estimate is ready to view.
                    </div>
                  </v-col>
                  <v-col v-if="isDataBricksRunning" cols="12" sm="4">
                    <v-row no-gutters>
                      <v-col cols="12">Loading Information...</v-col>
                      <v-col cols="12"
                        ><asc-page-loading-indicator
                          :loading="isDataBricksRunning"
                      /></v-col>
                    </v-row>
                  </v-col>
                  <v-col
                    v-if="isDataBricksRunning"
                    style="padding-left: 75"
                    cols="12"
                    sm="4"
                  >
                    <asc-accent-button
                      class="cancel-button-width"
                      secondary
                      block
                      button-text="Cancel"
                      :on-click="handleCancelPerformanceAnalysis"
                      :loading="validateLoading"
                      :disabled="!isLookBackValid"
                    >
                    </asc-accent-button>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" sm="4">
                    <div class="performanceLimitCount">
                      {{ runCount }} / {{ runLimit }} Performance estimates this
                      week.
                    </div></v-col
                  >
                  <v-col v-if="getPerformanceLimitReached">
                    <div class="performanceLimitTextMsg">
                      <v-icon class="alertIconColor">{{ 'mdi-alert' }}</v-icon>
                      You have hit your weekly limit. Your limitation will
                      refresh in {{ getDaysUntilRefresh }} days on
                      {{ getRefreshDate }}.
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscHeader from '@/components/atoms/AscHeader'
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscCustomAlertMixin from '@/components/mixins/AscCustomAlertMixin.vue'
import AscNotification from '@/components/molecules/AscNotification.vue'
// eslint-disable-next-line no-unused-vars
import { CUSTOM_ALERT_CONSTS as CAC } from '@/stores/consts'
import { getFormattedUTCDate } from '@/utilities/dates'
import AscTooltip from '@/components/atoms/AscTooltip'

import moment from 'moment'
import AscLabel from '../atoms/AscLabel.vue'

export default {
  name: 'AscPerformanceEstimateWrapper',

  components: {
    AscAccentButton,
    AscHeader,
    AscLabel,
    AscPageLoadingIndicator,
    AscNotification,
    AscTooltip,
  },

  mixins: [AscCustomAlertMixin],

  props: {
    estimateButtonName: {
      type: String,
      default: '',
    },
    lookBackResponse: {
      type: Object,
      default: () => {},
    },
    enableRunPerformanceEstimate: {
      type: Boolean,
      default: false,
    },
    logicStringValid: {
      type: Boolean,
      default: true,
    },
    isLookBackValid: {
      type: Boolean,
      default: false,
    },
    isDataBricksRunning: {
      type: Boolean,
      default: false,
    },
    alertData: {
      type: Object,
      default: () => {},
    },
    runLimitBeforeFirstSave: {
      type: Number,
      default: 0,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      validateLoading: false,
      selectedDate: '',
    }
  },

  computed: {
    isLookBackReadOnly() {
      return (
        this.isReadOnly &&
        this.estimateButtonName === CAC.RUN_PERFORMANCE_ESTIMATE
      )
    },

    displaySuccessMsg() {
      return this.estimateButtonName === CAC.SEE_PERFORMANCE_ESTIMATE
        ? true
        : false
    },
    error() {
      return [
        {
          text: 'Processing Error: There was an error while running your performance analysis. If you continue to experience this error, please contact support at ASCENTIA@collins.com',
          mode: 'alert',
          type: 'error',
          dismissible: true,
          isActive: true,
          color: 'error',
        },
      ]
    },
    disableRunPerformanceEstimate() {
      return !this.isLookBackValid && !this.isDataBricksRunning
    },
    estimateStartDate() {
      return moment().subtract(6, 'months').format('DD-MM-YYYY')
    },
    estimateEndDate() {
      return moment().format('DD-MM-YYYY')
    },
    getEstimateMessage() {
      return 'Generate a performance estimate for your custom analytic based on historical data.'
    },
    getLimitaionMessage() {
      return 'Performance Analysis Limitations'
    },
    getLookbackValidationMessage() {
      return 'Your logic must contain only 1 feature in order to run a performance estimate.'
    },
    getLookbackReadOnlyMessage() {
      return 'The creater of this analytic has not generated a performance estimate'
    },
    showLookbackWarning() {
      return (
        Object.hasOwn(this.alertData, 'isLookbackValid') &&
        this.isLookBackValid === false
      )
    },
    runCount() {
      return Object.prototype.hasOwnProperty.call(
        this.alertData,
        'lookbackLimit',
      )
        ? this.alertData.lookbackLimit?.runCount
        : 0
    },
    runLimit() {
      return Object.prototype.hasOwnProperty.call(
        this.alertData,
        'lookbackLimit',
      )
        ? this.alertData.lookbackLimit?.runLimit
        : this.runLimitBeforeFirstSave
    },
    getDaysUntilRefresh() {
      const now = new Date()
      const daysUntilReset = (7 - now.getUTCDay()) % 7
      const nextSundayUtc = new Date(now)
      nextSundayUtc.setUTCDate(now.getUTCDate() + daysUntilReset)
      nextSundayUtc.setUTCHours(0, 0, 0, 0)

      const diffDays = Math.ceil((nextSundayUtc - now) / (1000 * 60 * 60 * 24))
      return diffDays
    },
    getRefreshDate() {
      const now = new Date()
      const daysUntilReset = (7 - now.getUTCDay()) % 7
      const nextSundayUtc = new Date(now)
      nextSundayUtc.setUTCDate(now.getUTCDate() + daysUntilReset)
      nextSundayUtc.setUTCHours(0, 0, 0, 0)

      return getFormattedUTCDate(nextSundayUtc)
    },
    getPerformanceLimitReached() {
      return this.runCount >= this.runLimit && this.runCount
    },
    runLimitTooltipMsg() {
      return 'You have exceeded your performance estimate limit.'
    },
  },

  methods: {
    onPerformanceAnanlysis() {
      let date = {
        startDate: this.estimateStartDate,
        endDate: this.estimateEndDate,
      }
      this.$emit('onPerformanceAnanlysis', date)
    },
    handleCancelPerformanceAnalysis() {
      this.$emit('onCancelPerformanceAnalysis')
    },
    handleSeeEstimate() {
      this.$emit('onSeeEstimateDash')
    },
    databricksResponseErrorClose() {
      this.$emit('onDatabricksErrorNotificationClose')
    },
    handlePerformanceEstimateClick() {
      if (this.displaySuccessMsg) {
        this.$emit('onSeeEstimateDash')
      } else {
        this.onPerformanceAnanlysis()
      }
    },
  },
}
</script>

<style lang="scss">
.tooltip-content-icon {
  &::before {
    color: $accent-500;
    font-size: 12px;
    margin-left: -10px;
    margin-right: 5px;
  }
}
.tooltip-content-icon-text {
  margin-left: 5px;
}
.tooltip-content-text {
  margin-top: 5px;
}

.performance-estimate-wrapper {
  border: 1px solid $gray-100;
  padding-top: 1rem;

  .performance-estimate-header {
    padding-left: 1rem;
  }

  .asc-notification-wrapper {
    .content-wrapper {
      font-size: 13px !important;
    }
  }
  .estimate-message {
    margin-top: 25px;
    margin-bottom: 20px;
    padding-left: 1rem;

    .limitation-text-accent {
      color: $accent-700;
    }

    .icon-info-limit {
      color: $accent-500;
      cursor: pointer;
      font-size: $font-size-md;

      .tooltip-text {
        font-size: $font-size-sm;
      }
    }
  }

  .labelColor {
    color: $gray-700;
  }

  .performance-estimate-style {
    margin-top: 25px !important;
    margin-left: 0;
    background-color: $gray-100;
    padding: 1rem !important;
    .successTextMsg {
      padding-top: 10px;
      .checkIconColor {
        color: $accent-300;
      }
    }
    .button-width {
      width: 75%;
    }
    .cancel-button-width {
      width: 40%;
    }
    .performanceLimitCount {
      padding-top: 10px;
      font-size: 14px;
      font-style: italic;
    }
    .performanceLimitTextMsg {
      padding-top: 10px;
      color: $semantic-warning;
      font-size: 14px;
      font-style: italic;
      .alertIconColor {
        color: $semantic-warning;
      }
    }
  }

  .lookback-validation {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 16px;
    margin-top: 16px;
    height: 48px;
    background: $gray-0;
  }
}
</style>
